import React, { useState, useEffect, useCallback } from "react";
import { Activity, MessageSquare, AlertTriangle, CheckCircle, XCircle, RefreshCw, Clock, Users, TrendingUp } from "lucide-react";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatbotStatus = ({ authAxios }) => {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, healthRes] = await Promise.all([
        authAxios.get(`${API_URL}/api/admin/chatbot-stats`),
        fetch(`${API_URL}/api/whatsapp/health`).then(r => r.json()),
      ]);
      setStats(statsRes.data);
      setHealth(healthRes);
      setLastRefresh(new Date());
    } catch (err) {
      logger.error("Failed to fetch chatbot status:", err);
    }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const StatusDot = ({ ok }) => (
    <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-emerald-400" : "bg-red-400"} animate-pulse`} />
  );

  if (loading && !stats) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-6 animate-pulse" data-testid="chatbot-status-loading">
        <div className="h-5 bg-gray-200 rounded w-48 mb-4" />
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const { stats_24h, stats_7d, last_failure, system } = stats;
  const isOperational = system?.status === "operational";

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="chatbot-status">
      {/* Header */}
      <div className={`px-5 py-3.5 flex items-center justify-between ${isOperational ? "bg-emerald-600" : "bg-red-600"}`}>
        <div className="flex items-center gap-2.5">
          <Activity size={18} className="text-white" />
          <span className="text-white font-semibold text-sm">Chatbot Monitor</span>
          <span className={`ml-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${isOperational ? "bg-emerald-400/30 text-emerald-100" : "bg-red-400/30 text-red-100"}`}>
            {isOperational ? "LIVE" : "DEGRADED"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {lastRefresh && (
            <span className="text-white/60 text-[11px]">
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="chatbot-refresh-btn"
          >
            <RefreshCw size={14} className={`text-white ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* System Health Row */}
        <div className="flex gap-3">
          {health && (
            <>
              <HealthChip label="Twilio" ok={health.checks?.twilio?.status === "ok"} />
              <HealthChip label="AI (LLM)" ok={health.checks?.llm?.status === "ok"} />
              <HealthChip label="Database" ok={health.checks?.mongodb?.status === "ok"} />
            </>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={<MessageSquare size={16} />}
            label="Messages (24h)"
            value={stats_24h.inbound}
            color="text-blue-600"
            bg="bg-blue-50"
            testId="stat-messages-24h"
          />
          <StatCard
            icon={<Users size={16} />}
            label="Conversations"
            value={stats_24h.unique_conversations}
            color="text-violet-600"
            bg="bg-violet-50"
            testId="stat-conversations"
          />
          <StatCard
            icon={<TrendingUp size={16} />}
            label="Success Rate"
            value={`${stats_24h.success_rate}%`}
            color={stats_24h.success_rate >= 90 ? "text-emerald-600" : "text-amber-600"}
            bg={stats_24h.success_rate >= 90 ? "bg-emerald-50" : "bg-amber-50"}
            testId="stat-success-rate"
          />
          <StatCard
            icon={<AlertTriangle size={16} />}
            label="Failed (24h)"
            value={stats_24h.failed}
            color={stats_24h.failed === 0 ? "text-emerald-600" : "text-red-600"}
            bg={stats_24h.failed === 0 ? "bg-emerald-50" : "bg-red-50"}
            testId="stat-failed-24h"
          />
        </div>

        {/* 7-day summary */}
        <div className="flex items-center gap-4 text-xs text-gray-500 px-1">
          <span className="flex items-center gap-1">
            <Clock size={12} />
            7-day: {stats_7d.inbound} messages, {stats_7d.failed} failures
          </span>
        </div>

        {/* Last Failure Alert */}
        {last_failure?.phone && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-3" data-testid="last-failure-alert">
            <div className="flex items-start gap-2">
              <XCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
              <div className="text-xs">
                <p className="text-red-700 font-medium">Last failure: {last_failure.phone}</p>
                <p className="text-red-500 mt-0.5 truncate">{last_failure.error}</p>
                <p className="text-red-400 mt-0.5">
                  {last_failure.timestamp ? new Date(last_failure.timestamp).toLocaleString() : ""}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const HealthChip = ({ label, ok }) => (
  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid={`health-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>
    {ok ? <CheckCircle size={12} /> : <XCircle size={12} />}
    {label}
  </div>
);

const StatCard = ({ icon, label, value, color, bg, testId }) => (
  <div className={`${bg} rounded-xl p-3`} data-testid={testId}>
    <div className={`${color} flex items-center gap-1.5 mb-1.5`}>
      {icon}
      <span className="text-[11px] font-medium opacity-80">{label}</span>
    </div>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
  </div>
);

export default ChatbotStatus;
